// ── BILLING HISTORY V2 ────────────────────────────────────────────────────────
// Cross-project archive of every billing package that has gone to a client
// (billing_summaries rows with status sent or paid), plus a per-package
// preview whose defining feature is "Everything the client received": each
// document that rode the package with its own as-sent PDF download, and a
// "Download full package" zip that re-assembles the exact envelope.
//
// Since 2026-07-02 this is ALSO the place payments get recorded (Cimon:
// "Billing history should be the place to record payment" — the workbench
// placement was confusing). The "Record payment" button in the preview's
// upper-right status area launches RecordClientPaymentWizard against the
// package's underlying documents; everything else stays read-only.
//
// Two hard rules govern every number on this screen:
//
//   1. Payment truth comes ONLY from payment_allocations (Architectural
//      Rule 10). ASKED / PAID / BALANCE and every per-document status are
//      derived live via the pure helpers in utils.js — never read from
//      status strings or stored paid amounts.
//   2. The component renders; it does not compute money. All derivation
//      lives in resolveBillingPackage / resolveBillingPackagePayments /
//      buildPackagePaymentTargets / buildPackageZipPlan (utils.js), each
//      unit-tested.
//
// Kept from v1 (Phase 5A mechanics, pinned by documentSendsWiring.test.js):
// frozen-payload cover regeneration, the as-sent stored-PDF download, and
// the Send History pills.
//
// props:
//   onClientPaymentRecorded — (savedPayment, savedAllocations) => void|Promise.
//     The caller's cascade dispatcher (InvoicesTab's onBankDepositSaved) —
//     fires syncInvoiceStatusFromAllocations → cascadePaidItems so the
//     deposit→item→PO cascade runs. Same contract BankDepositModal uses.

import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAppContext } from './AppContext';
import {
  fmtMoney,
  latestDocumentSend,
  documentSendsFor,
  resolveBillingPackage,
  resolveBillingPackagePayments,
  buildPackagePaymentTargets,
  packagePendingDepositAsks,
  buildPackageZipPlan,
  billingPaidStateLabel,
  billingDocPaidLabel,
  buildSummaryReceiptLines,
  applyExpenseGroupsToReceiptLines,
  normalizeExpensesDisplay,
  expenseGroupMembers,
  buildProposalPayloadPure,
  hasAdminAccess,
} from '../utils';
import RecordClientPaymentWizard from './RecordClientPaymentWizard';
import {
  INK_1, INK_2, INK_3, INK_4,
  SURFACE, GROUND_OFF,
  ZONE_YELLOW, ZONE_FUCHSIA, ZONE_GREEN, ZONE_GREY,
  ALERT_AMBER_TEXT, ALERT_RED_TEXT,
  HAIRLINE, HAIRLINE_ROW, HAIRLINE_1PX, HAIRLINE_THICK_INK,
  TABULAR,
} from '../lib/designTokens';

// Package-state → dot color (mockup legend: green / fuchsia / yellow / grey).
const STATE_DOT = {
  paid:    ZONE_GREEN,
  partial: ZONE_FUCHSIA,
  unpaid:  ZONE_YELLOW,
  none:    ZONE_GREY,
};
// Package-state → text color. Awaiting-payment copy reads in amber TEXT
// (never a yellow fill — hairlines + colored text on white per CLAUDE.md).
const STATE_TEXT = {
  paid:    ZONE_GREEN,
  partial: ZONE_FUCHSIA,
  unpaid:  ALERT_AMBER_TEXT,
  none:    INK_3,
};
// Per-document derived status ('paid' | 'partial' | anything else) → color.
function docStatusColor(status) {
  var s = String(status || '').toLowerCase();
  if (s === 'paid') return ZONE_GREEN;
  if (s === 'partial') return ZONE_FUCHSIA;
  return ALERT_AMBER_TEXT;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    var s = String(iso);
    var m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    var d = m ? new Date(parseInt(m[1]), parseInt(m[2]) - 1, parseInt(m[3])) : new Date(s);
    if (isNaN(d.getTime())) return s.slice(0, 10);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch (e) { return String(iso).slice(0, 10); }
}

function periodLabel(month, year) {
  var months = ['', 'Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return (months[month] || '') + ' ' + (year || '');
}

function parseJsonish(v, fallback) {
  if (v == null) return fallback;
  if (typeof v !== 'string') return v;
  try { return JSON.parse(v); } catch (e) { return fallback; }
}

// Money cell: em-dash for zero (mockup: paid rows show — in BALANCE).
function fmtDash(n) {
  var v = parseFloat(n) || 0;
  return v > 0.005 ? fmtMoney(v) : '—';
}

// Source of a document's download, read off its zip-plan entry's FIRST
// attempt (the plan orders attempts stored → regen → live).
//   'stored' — the exact bytes the client received
//   'regen'  — re-rendered from a frozen payload / snapshot
//   'live'   — rebuilt from live data (oldest packages only)
function entrySource(entry) {
  if (!entry || !entry.attempts || entry.attempts.length === 0) return 'unavailable';
  return entry.attempts[0].via;
}
function entrySourceLabel(entry) {
  var v = entrySource(entry);
  if (v === 'stored') return '';               // as-sent is the default; no caveat
  if (v === 'regen') return 'regenerated';
  if (v === 'live') return 'from live data';
  return 'unavailable';
}

export default function BillingHistorySection(props) {
  const ctx = useAppContext();
  const projects  = ctx.projects  || [];
  const proposals = ctx.proposals || [];
  const invoices  = ctx.invoices  || [];
  const clients   = ctx.clients   || [];
  const receipts  = ctx.receipts  || [];
  const allItems  = ctx.items     || [];
  const paymentAllocations = ctx.paymentAllocations || [];
  const clientPayments     = ctx.clientPayments     || [];
  const retainerLedger     = ctx.retainerLedger     || [];
  const clientCreditLedger = ctx.clientCreditLedger || [];
  const userEmail    = ctx.userEmail || '';
  const isOwner      = !!ctx.isOwner;
  const firmSettings = ctx.firmSettings || {};
  // Same gate as the rest of the billing surface (owner OR admin).
  const canRecordPayment = isOwner || hasAdminAccess(userEmail, firmSettings);

  const [packages, setPackages] = useState([]);
  const [hoursInvoices, setHoursInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Archive filters — project, client, paid-state chips, search.
  const [projectFilter, setProjectFilter] = useState('all');
  const [clientFilter, setClientFilter]   = useState('all');
  const [stateFilter, setStateFilter]     = useState('all');
  const [search, setSearch]               = useState('');

  // Which package's preview is open (null = archive list).
  const [selectedId, setSelectedId] = useState(null);

  // Cover-PDF preview modal state (kept from v1).
  const [previewing, setPreviewing] = useState(null); // { url, ref, loading, err, summary, frozen?, latestSend? }

  // Full-package zip assembly state.
  const [zipBusy, setZipBusy] = useState(false);
  const [zipNote, setZipNote] = useState('');
  // Per-document download-in-flight keys (plan entry filenames).
  const [downloadBusy, setDownloadBusy] = useState({});

  // Which combined-expense lines are disclosed, keyed by group id. Purely a
  // per-viewer reading convenience — never persisted; a group's membership
  // lives on the summary's expenses_display row, not here.
  const [openExpenseGroups, setOpenExpenseGroups] = useState({});

  // Record Client Payment wizard state. wizardPdf = { url, label } | null —
  // the as-sent cover for the wizard's side-by-side pane, signed at open
  // time (moved here from BillingSummarySection.openPaymentWizard when the
  // recording surface moved, 2026-07-02).
  const [wizardOpen, setWizardOpen] = useState(false);
  const [wizardPdf, setWizardPdf] = useState(null);

  useEffect(function() { loadArchive(); }, []);

  async function loadArchive() {
    setLoading(true);
    // billing_summaries — same read as v1: every sent/paid row, all
    // projects, newest first.
    const summariesQ = await supabase.from('billing_summaries')
      .select('*')
      .in('status', ['sent', 'paid'])
      .order('sent_at', { ascending: false })
      .limit(200);
    if (summariesQ.error) { setError(summariesQ.error.message); setLoading(false); return; }
    setPackages(summariesQ.data || []);
    // hours_invoices isn't loaded in AppContext (no other surface needs the
    // whole table), so this section queries it directly — same pattern v1
    // used for billing_summaries. Snapshot columns ride along so the zip's
    // regen fallback can rebuild the PDF from the row itself.
    const hoursQ = await supabase.from('hours_invoices')
      .select('invoice_number, project_label, client_name, invoice_date, status, total_hours, total_amount, sent_at, categories_snapshot, adjusted_hours, summaries');
    if (hoursQ.error) {
      // Non-fatal: the archive still works; hours rows just won't resolve.
      console.warn('hours_invoices load failed:', hoursQ.error.message);
    }
    setHoursInvoices(hoursQ.data || []);
    setLoading(false);
  }

  // ── DERIVATION — all money via the pure helpers ────────────────────────
  // documentSends rides along so resolveBillingPackage can read the frozen
  // send manifest: the deposit-ask fallback chain (proposals ride summaries
  // BEFORE approval mints their per-item DEPs) and the send-record
  // reconciliation of untagged invoices both depend on it.
  const resolvedPackages = useMemo(function() {
    return packages.map(function(s) {
      return resolveBillingPackage(s, {
        proposals: proposals,
        invoices: invoices,
        receipts: receipts,
        allocations: paymentAllocations,
        payments: clientPayments,
        retainerLedger: retainerLedger,
        hoursInvoices: hoursInvoices,
        clientCreditLedger: clientCreditLedger,
        documentSends: ctx.documentSends || [],
      });
    });
  }, [packages, proposals, invoices, receipts, paymentAllocations, clientPayments, retainerLedger, hoursInvoices, clientCreditLedger, ctx.documentSends]);

  const projectOptions = useMemo(function() {
    var seen = {};
    resolvedPackages.forEach(function(p) { if (p.projectLabel) seen[p.projectLabel] = true; });
    return Object.keys(seen).sort();
  }, [resolvedPackages]);

  const clientOptions = useMemo(function() {
    var seen = {};
    resolvedPackages.forEach(function(p) { if (p.clientName) seen[p.clientName] = true; });
    return Object.keys(seen).sort();
  }, [resolvedPackages]);

  const filteredPackages = useMemo(function() {
    var q = search.trim().toLowerCase();
    return resolvedPackages.filter(function(p) {
      if (projectFilter !== 'all' && p.projectLabel !== projectFilter) return false;
      if (clientFilter !== 'all' && p.clientName !== clientFilter) return false;
      if (stateFilter !== 'all' && p.state !== stateFilter) return false;
      if (q) {
        var hay = (p.projectLabel + ' ' + p.clientName + ' ' + p.periodLabel).toLowerCase();
        if (hay.indexOf(q) < 0) return false;
      }
      return true;
    });
  }, [resolvedPackages, projectFilter, clientFilter, stateFilter, search]);

  const openBalanceTotal = useMemo(function() {
    return Math.round(filteredPackages.reduce(function(s, p) { return s + p.balance; }, 0) * 100) / 100;
  }, [filteredPackages]);

  const selectedPkg = selectedId
    ? resolvedPackages.find(function(p) { return p.summaryId === String(selectedId); }) || null
    : null;

  const paymentCards = useMemo(function() {
    if (!selectedPkg) return [];
    return resolveBillingPackagePayments(selectedPkg, {
      payments: clientPayments,
      allocations: paymentAllocations,
      proposals: proposals,
    });
  }, [selectedPkg, clientPayments, paymentAllocations, proposals]);

  // Allocatable targets for the Record Client Payment wizard — the pure
  // helper walks the SAME resolved package the money strip reads, and the
  // memo depends on live ctx allocations, so a saved payment re-derives
  // both the strip and the remaining targets without a reload.
  const paymentTargets = useMemo(function() {
    if (!selectedPkg) return [];
    return buildPackagePaymentTargets(selectedPkg, {
      items: allItems,
      allocations: paymentAllocations,
    });
  }, [selectedPkg, allItems, paymentAllocations]);

  // The zip plan doubles as the per-document download map — every "↓ PDF"
  // button on the preview executes its entry's attempts in plan order
  // (stored bytes first, frozen-payload regen second, live rebuild last),
  // so a row download and the full-package zip can never disagree about
  // which file the bookkeeper gets.
  const zipPlan = useMemo(function() {
    if (!selectedPkg) return null;
    // Full refs: the invoice regen fallback needs them to reproduce the
    // PDF that was issued (addresses, header PO, cross-reference block,
    // payment breakdown) rather than a stripped-down version of it.
    return buildPackageZipPlan(selectedPkg, ctx.documentSends || [], {
      clients: ctx.clients || [], projects: ctx.projects || [],
      items: ctx.items || [], proposals: ctx.proposals || [],
      purchaseOrders: ctx.purchaseOrders || [], invoices: ctx.invoices || [],
      billingSummaries: ctx.billingSummaries || [],
      paymentAllocations: ctx.paymentAllocations || [],
      clientPayments: ctx.clientPayments || [],
    });
  }, [selectedPkg, ctx.documentSends]);

  function planEntryFor(kind, id) {
    if (!zipPlan) return null;
    return zipPlan.entries.find(function(e) {
      return e.kind === kind && (id == null || String(e.id) === String(id));
    }) || null;
  }

  // Open the Record Client Payment wizard, resolving the as-sent cover PDF
  // first so the bookkeeper records payments while looking at exactly what
  // the client was asked to pay. Best-effort: any lookup/signing failure
  // just opens the wizard without the document pane. (Pattern moved intact
  // from BillingSummarySection.openPaymentWizard, 2026-07-02.)
  async function openRecordPaymentWizard(pkg) {
    let pdf = null;
    try {
      const send = pkg && pkg.summaryId
        ? latestDocumentSend(ctx.documentSends || [], 'billing_summary', pkg.summaryId)
        : null;
      if (send && send.storagePath) {
        const relPath = String(send.storagePath).replace(/^billing-summaries\//, '');
        const { data, error } = await supabase.storage
          .from('billing-summaries')
          .createSignedUrl(relPath, 3600);
        if (!error && data && data.signedUrl) {
          pdf = {
            url: data.signedUrl,
            label: 'As sent to client · ' + pkg.monthYear,
          };
        }
      }
    } catch (e) { /* pane is optional — open without it */ }
    setWizardPdf(pdf);
    setWizardOpen(true);
  }

  // The wizard's retainer story (five-step redesign, 2026-07-10). Null on
  // non-retainer projects. Carries the summary's ask, what's been received
  // (the package's date-heuristic match), and the draw that covered the
  // period's services — amount + date from the ledger entry the send wrote
  // (sourceType 'billing_summary'), lines from the same summary-row fields
  // the package preview's services block renders.
  function buildWizardRetainerProps(pkg) {
    if (!pkg || !pkg.hasRetainer || !pkg.retainer) return null;
    var s = pkg.summary || {};
    var drawEntry = (retainerLedger || []).find(function(en) {
      return en && en.sourceType === 'billing_summary' && String(en.sourceId || '') === String(pkg.summaryId);
    }) || null;
    var lines = [];
    if (pkg.hoursRow) {
      var hrs = pkg.hoursRow.hoursInvoice && pkg.hoursRow.hoursInvoice.total_hours;
      lines.push({
        label: 'Design services — ' + pkg.monthYear + (hrs ? ' (' + hrs + ' hrs)' : ''),
        amount: pkg.hoursRow.asked,
      });
    } else if ((parseFloat(s.design_amount) || 0) > 0.005) {
      var dh = parseFloat(s.design_hours) || 0;
      lines.push({
        label: 'Design services — ' + pkg.monthYear + (dh > 0 ? ' (' + dh + ' hrs)' : ''),
        amount: parseFloat(s.design_amount) || 0,
      });
    }
    (parseJsonish(s.additional_services, []) || []).forEach(function(a) {
      if (a && (parseFloat(a.amount) || 0) > 0.005) lines.push({ label: a.name || 'Additional service', amount: parseFloat(a.amount) || 0 });
    });
    (parseJsonish(s.expenses, []) || []).forEach(function(e) {
      if (e && (parseFloat(e.amount) || 0) > 0.005) lines.push({ label: e.description || 'Expense', amount: parseFloat(e.amount) || 0 });
    });
    buildSummaryReceiptLines(parseJsonish(s.selected_receipt_ids, []) || [], receipts).forEach(function(ln) {
      if (ln && ln.amount > 0.005) lines.push({ label: ln.description || 'Reimbursable expense', amount: ln.amount });
    });
    return {
      requested: pkg.retainer.requested,
      received: pkg.retainer.received,
      drawAmount: drawEntry ? Math.abs(parseFloat(drawEntry.amount) || 0) : null,
      drawDate: drawEntry ? (drawEntry.entryDate || null) : null,
      drawLines: lines,
    };
  }

  // Package documents that carried payments from BEFORE this deposit —
  // the wizard's leftover explanation ("the cover billed these in full,
  // so paying the cover total pays them twice").
  function buildWizardAlreadyPaidLines(pkg) {
    if (!pkg) return [];
    var lines = [];
    (pkg.depositRows || []).forEach(function(row) {
      if (row && row.paid > 0.005) {
        var p = row.proposal || {};
        lines.push({ label: (p.name || p.proposal_ref || 'Proposal') + ' — deposit', amount: row.paid });
      }
    });
    (pkg.invoiceRows || []).forEach(function(row) {
      if (row && row.paid > 0.005) lines.push({ label: row.name || row.ref || 'Invoice', amount: row.paid });
    });
    return lines;
  }

  // ── LEGACY COVER PAYLOAD (live rebuild) ────────────────────────────────
  // Rebuilds the cover payload from the summary row's snapshot columns +
  // current live state. Used ONLY when no document_sends row exists
  // (pre-Phase-5A sends) — for the modal's fallback path and the zip's
  // last-resort 'live' attempt. Kept byte-for-byte from v1.
  function buildLegacyCoverPayload(summary) {
    const proj = projects.find(p => p.label === summary.project_label);
    const monthName = ['', 'January','February','March','April','May','June','July','August','September','October','November','December'][summary.month] || '';
    const monthNames = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    let dateStr;
    const sentDate = summary.sent_at ? new Date(summary.sent_at) : null;
    if (sentDate && !isNaN(sentDate.getTime())) {
      dateStr = `${monthNames[sentDate.getMonth()]} ${sentDate.getDate()}, ${sentDate.getFullYear()}`;
    } else {
      const today = new Date();
      dateStr = `${monthNames[today.getMonth()]} ${today.getDate()}, ${today.getFullYear()}`;
    }
    const projClient = (proj && proj.clientId)
      ? clients.find(c => c.id === proj.clientId)
      : clients.find(c => c.name === summary.client_name);
    const addl = parseJsonish(summary.additional_services, []) || [];
    const exp  = parseJsonish(summary.expenses, []) || [];
    const procurement = parseJsonish(summary.procurement, {}) || {};
    const proposalsThisMonth = parseJsonish(summary.proposals_this_month, []) || [];
    const invoicesThisMonth  = parseJsonish(summary.invoices_this_month,  []) || [];
    const hoursNarratives = parseJsonish(summary.hours_narratives, []) || [];
    return {
      date: dateStr,
      billTo: {
        name:    summary.client_name || (projClient && projClient.name) || '',
        address: (projClient && projClient.billingAddress) || '',
        city:    (projClient && projClient.billingCity)    || '',
        state:   (projClient && projClient.billingState)   || '',
        zip:     (projClient && projClient.billingZip)     || '',
      },
      forPeriod: monthName + ' ' + summary.year,
      designServices: { hours: parseFloat(summary.design_hours) || 0, amount: parseFloat(summary.design_amount) || 0 },
      additionalServices: addl,
      expenses: exp,
      billingMethod: summary.has_retainer ? 'retainer' : 'hourly',
      retainerBalance: parseFloat(summary.retainer_balance_before) || 0,
      retainerRequest: summary.has_retainer ? (parseFloat(summary.retainer_request) || 0) : 0,
      hoursNarratives: hoursNarratives,
      procurement: {
        proposalTotal:    procurement.proposalTotal    || 0,
        depositRequested: procurement.depositRequested || 0,
        invoicesDue:      procurement.invoicesDue      || 0,
      },
      proposalsThisMonth: (proposalsThisMonth || []).map(p => ({
        ref: p.ref || '', name: p.name || '', sentDate: p.sent_date || '',
        totalDeposit: p.total_deposit, totalClient: p.total_client,
        status: p.status, isOutstanding: p.is_outstanding, amountOwed: p.amount_owed,
      })),
      invoicesThisMonth: (invoicesThisMonth || []).map(i => ({
        ref: i.ref || '', type: i.type || '', sentDate: i.sent_date || '',
        amount: i.amount, status: i.status,
      })),
    };
  }

  // Live proposal payload — last-resort rebuild for proposals whose send
  // predates the document_sends 'proposal' archive (before 2026-07-02).
  // Labeled "from live data" everywhere it surfaces: post-send item edits
  // WILL show, which is exactly why the label exists.
  function buildLiveProposalPayload(p) {
    if (!p) return null;
    const pItems = (p.items || [])
      .map(id => allItems.find(it => it.id === id))
      .filter(Boolean);
    const projObj = projects.find(pr => pr.label === p.project) || { label: p.project };
    const client = clients.find(c => c.id === p.clientId)
      || (projObj.clientId ? clients.find(c => c.id === projObj.clientId) : null)
      || {};
    return buildProposalPayloadPure({
      proposal: p,
      items:    pItems,
      client:   client,
      project:  projObj,
      allItems: allItems,
    });
  }

  // ── DOWNLOAD EXECUTOR — walks a plan entry's attempts in order ─────────
  async function fetchAttemptBytes(attempt, pkg) {
    if (attempt.via === 'stored') {
      const { data, error } = await supabase.storage
        .from(attempt.bucket)
        .createSignedUrl(attempt.path, 600);
      if (error || !data || !data.signedUrl) throw new Error((error && error.message) || 'No signed URL');
      const res = await fetch(data.signedUrl);
      if (!res.ok) throw new Error('Stored PDF fetch failed');
      return { bytes: new Uint8Array(await res.arrayBuffer()), via: 'stored' };
    }
    if (attempt.via === 'regen' || attempt.via === 'live') {
      let endpoint = attempt.endpoint;
      let payload  = attempt.payload;
      let parse    = attempt.parse || 'blob';
      if (attempt.via === 'live') {
        if (attempt.kind === 'cover') {
          endpoint = '/api/generate-billing-summary';
          payload  = buildLegacyCoverPayload(pkg.summary);
          parse    = 'json';
        } else if (attempt.kind === 'proposal') {
          const p = proposals.find(function(x) { return String(x.id) === String(attempt.proposalId); });
          endpoint = '/api/generate-proposal';
          payload  = buildLiveProposalPayload(p);
          parse    = 'blob';
        }
      }
      if (!endpoint || !payload) throw new Error('Nothing to regenerate from');
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const err = await res.json().catch(function() { return {}; });
        throw new Error(err.error || (endpoint + ' failed'));
      }
      if (parse === 'json') {
        const data = await res.json();
        return { bytes: Uint8Array.from(atob(data.pdf), c => c.charCodeAt(0)), via: attempt.via };
      }
      const blob = await res.blob();
      return { bytes: new Uint8Array(await blob.arrayBuffer()), via: attempt.via };
    }
    throw new Error('Unknown attempt kind');
  }

  async function resolveEntryBytes(entry, pkg) {
    let lastErr = null;
    for (const attempt of entry.attempts) {
      try {
        return await fetchAttemptBytes(attempt, pkg);
      } catch (e) { lastErr = e; }
    }
    throw lastErr || new Error('No source available');
  }

  async function downloadEntry(entry, pkg) {
    if (!entry) { window.alert('No document available.'); return; }
    setDownloadBusy(function(prev) { var n = Object.assign({}, prev); n[entry.filename] = true; return n; });
    try {
      const r = await resolveEntryBytes(entry, pkg);
      const blob = new Blob([r.bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = entry.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      window.alert('Download failed: ' + (e.message || 'unknown'));
    }
    setDownloadBusy(function(prev) { var n = Object.assign({}, prev); delete n[entry.filename]; return n; });
  }

  // ── FULL-PACKAGE ZIP ───────────────────────────────────────────────────
  // Same CDN-load pattern as BillingSummarySection.loadJSZip.
  async function loadJSZip() {
    if (window.JSZip) return window.JSZip;
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jszip/3.10.1/jszip.min.js';
      script.onload = () => resolve(window.JSZip);
      script.onerror = () => reject(new Error('Could not load JSZip'));
      document.head.appendChild(script);
    });
  }

  async function downloadFullPackage(pkg) {
    if (!pkg || !zipPlan) return;
    setZipBusy(true);
    setZipNote('');
    try {
      const JSZip = await loadJSZip();
      const zip = new JSZip();
      const skipped = [];
      for (const entry of zipPlan.entries) {
        try {
          const r = await resolveEntryBytes(entry, pkg);
          zip.file(entry.filename, r.bytes);
        } catch (e) {
          // Skip-and-note — never a silent drop.
          skipped.push(entry.label);
        }
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = zipPlan.zipName;
      a.click();
      URL.revokeObjectURL(url);
      if (skipped.length > 0) {
        setZipNote('Left out of the zip (no stored PDF and nothing to regenerate from): ' + skipped.join(' · '));
      }
    } catch (e) {
      setZipNote('Package download failed: ' + (e.message || 'unknown'));
    }
    setZipBusy(false);
  }

  // ── COVER PREVIEW MODAL (kept from v1) ─────────────────────────────────
  async function openCoverPdf(summary) {
    setPreviewing({ url: null, ref: '', loading: true, err: '', summary: summary });
    try {
      // Phase 5A — versioned send history read path. If this summary has
      // a document_sends row, regenerate the cover PDF from the frozen
      // payload verbatim. The summary's row may be mutated post-send by
      // unrelated edits; reading live state for cover regen is the April
      // 2026 retainer-draw drift mode. With a send record, the payload is
      // frozen at send time and any drift can never reach the regenerated
      // PDF.
      const latestSend = latestDocumentSend(
        ctx.documentSends || [],
        'billing_summary',
        summary.id);
      if (latestSend && latestSend.payload) {
        const res = await fetch('/api/generate-billing-summary', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(latestSend.payload),
        });
        const result = await res.json();
        if (!res.ok) throw new Error(result.error || 'PDF failed (frozen payload)');
        const bytes = Uint8Array.from(atob(result.pdf), c => c.charCodeAt(0));
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPreviewing({ url, ref: summary.id, loading: false, err: '', summary, frozen: true, latestSend });
        return;
      }

      // Legacy live-regen path — rebuild payload from snapshot columns +
      // current live state. Used when no document_sends row exists yet
      // (pre-Phase-5A historical summaries).
      const payload = buildLegacyCoverPayload(summary);
      const res = await fetch('/api/generate-billing-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'PDF failed');
      const bytes = Uint8Array.from(atob(result.pdf), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewing({ url, ref: summary.id, loading: false, err: '', summary });
    } catch (e) {
      setPreviewing({ url: null, ref: '', loading: false, err: e.message || 'Could not regenerate', summary });
    }
  }

  function closePreview() {
    if (previewing && previewing.url) URL.revokeObjectURL(previewing.url);
    setPreviewing(null);
  }

  // renderFrozenSnapshot: re-renders the cover iframe with a SPECIFIC
  // document_sends row's payload (e.g., comparing an earlier re-send).
  async function renderFrozenSnapshot(summary, send) {
    if (previewing && previewing.url) URL.revokeObjectURL(previewing.url);
    setPreviewing({ url: null, ref: '', loading: true, err: '', summary, frozen: true, latestSend: send });
    try {
      const res = await fetch('/api/generate-billing-summary', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(send.payload || {}),
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'PDF failed (frozen snapshot)');
      const bytes = Uint8Array.from(atob(result.pdf), c => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      setPreviewing({ url, ref: summary.id, loading: false, err: '', summary, frozen: true, latestSend: send });
    } catch (e) {
      setPreviewing({ url: null, ref: summary.id, loading: false, err: e.message || 'Could not render', summary, frozen: true, latestSend: send });
    }
  }

  // downloadStoredCover: fetches the audit-grade as-sent PDF blob from
  // the billing-summaries bucket via a signed URL — the exact bytes that
  // were uploaded at send time.
  async function downloadStoredCover(send) {
    if (!send || !send.storagePath) return;
    try {
      const relPath = String(send.storagePath).replace(/^billing-summaries\//, '');
      const { data, error } = await supabase.storage
        .from('billing-summaries')
        .createSignedUrl(relPath, 60);
      if (error) { window.alert('Download failed: ' + error.message); return; }
      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = ((selectedPkg && selectedPkg.projectLabel)
        || (previewing && previewing.summary && previewing.summary.project_label)
        || 'billing_summary') + '__as-sent.pdf';
      a.click();
    } catch (e) {
      window.alert('Download failed: ' + (e.message || 'unknown'));
    }
  }

  // ── RENDER ─────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: '20px 18px', maxWidth: 1200 }}>
      {error && <div style={{ color: ALERT_RED_TEXT, fontSize: 12, marginBottom: 12 }}>{error}</div>}

      {!selectedPkg ? renderArchive() : renderPreview(selectedPkg)}

      {renderCoverModal()}

      {/* Record Client Payment wizard — targets are the package's
          UNDERLYING documents (per-item DEPs shown by proposal name, FINs
          incl. send-record-linked rows, non-retainer hours invoice), built
          by buildPackagePaymentTargets from the same resolved package the
          money strip reads. onSaved is the caller's cascade dispatcher
          (InvoicesTab.onBankDepositSaved) so deposit→item→PO cascades
          fire; the strip re-derives from live ctx allocations on save. */}
      {wizardOpen && selectedPkg && (
        <RecordClientPaymentWizard
          packageLabel={selectedPkg.projectLabel + ' · ' + selectedPkg.monthYear}
          clientName={selectedPkg.clientName}
          monthYear={selectedPkg.monthYear}
          retainer={buildWizardRetainerProps(selectedPkg)}
          alreadyPaidLines={buildWizardAlreadyPaidLines(selectedPkg)}
          saveRetainerEntry={ctx.saveRetainerEntry}
          saveClientCreditEntry={ctx.saveClientCreditEntry}
          documentContext={{
            clientId: (function() {
              const proj = projects.find(function(pr) { return pr.label === selectedPkg.projectLabel; });
              return (proj && proj.clientId) || '';
            })(),
            projectLabel: selectedPkg.projectLabel,
          }}
          targets={paymentTargets}
          creditToPlace={selectedPkg.creditToPlace || 0}
          pendingDeposits={packagePendingDepositAsks(selectedPkg)}
          pdfUrl={wizardPdf ? wizardPdf.url : ''}
          pdfLabel={wizardPdf ? wizardPdf.label : ''}
          userEmail={userEmail}
          recordClientPayment={ctx.recordClientPayment}
          onSaved={props.onClientPaymentRecorded}
          onClose={function() { setWizardOpen(false); }} />
      )}
    </div>
  );

  // ── SCREEN 1 — the archive list ────────────────────────────────────────
  function renderArchive() {
    return (
      <div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
          <div style={{ fontWeight: 500, fontSize: 15, color: INK_1, letterSpacing: '-0.005em' }}>Billing History</div>
          <div style={{ fontSize: 11, color: INK_3 }}>
            Every billing package that has gone to a client — what was asked, what came in, what's still open. Click a row to see everything inside the package.
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
          <select value={projectFilter} onChange={function(e) { setProjectFilter(e.target.value); }} style={sel}>
            <option value="all">All projects</option>
            {projectOptions.map(function(label) { return <option key={label} value={label}>{label}</option>; })}
          </select>
          <select value={clientFilter} onChange={function(e) { setClientFilter(e.target.value); }} style={sel}>
            <option value="all">All clients</option>
            {clientOptions.map(function(name) { return <option key={name} value={name}>{name}</option>; })}
          </select>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
            {[
              { key: 'all',     label: 'All',              dot: null },
              { key: 'paid',    label: 'Paid in full',     dot: STATE_DOT.paid },
              { key: 'partial', label: 'Partially paid',   dot: STATE_DOT.partial },
              { key: 'unpaid',  label: 'Awaiting payment', dot: STATE_DOT.unpaid },
              { key: 'none',    label: 'No amount due',    dot: STATE_DOT.none },
            ].map(function(chip) {
              var active = stateFilter === chip.key;
              return (
                <button key={chip.key}
                  onClick={function() { setStateFilter(chip.key); }}
                  style={{
                    fontSize: 10, fontWeight: 500, fontFamily: 'inherit', cursor: 'pointer',
                    color: active ? INK_1 : INK_2, background: SURFACE,
                    border: active ? HAIRLINE_THICK_INK : HAIRLINE_1PX,
                    borderRadius: 10, padding: '3px 11px',
                    display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap',
                  }}>
                  {chip.dot && <span style={dotStyle(chip.dot)} />}
                  {chip.label}
                </button>
              );
            })}
          </div>
          <input value={search} onChange={function(e) { setSearch(e.target.value); }}
            placeholder="Search project, client, period…" autoCorrect="off" autoCapitalize="none" spellCheck={false} autoComplete="off"
            style={Object.assign({}, sel, { width: 190, cursor: 'text' })} />
          <span style={{ fontSize: 11, color: INK_3, marginLeft: 'auto', whiteSpace: 'nowrap' }}>
            <b style={{ color: INK_1, fontWeight: 500 }}>{filteredPackages.length} package{filteredPackages.length === 1 ? '' : 's'}</b>
            {' · '}
            <span style={{ fontVariantNumeric: 'tabular-nums', fontFeatureSettings: TABULAR }}>{fmtMoney(openBalanceTotal)}</span>
            {' still open'}{projectFilter === 'all' && clientFilter === 'all' ? ' across all projects' : ''}
          </span>
        </div>

        <div style={{ background: SURFACE, border: HAIRLINE_1PX, borderRadius: 5, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ minWidth: 920 }}>
              <div style={Object.assign({}, archiveGrid, { borderBottom: HAIRLINE, background: GROUND_OFF })}>
                <div style={ahead}></div>
                <div style={ahead}>Project</div>
                <div style={ahead}>Client</div>
                <div style={ahead}>Period / Kind</div>
                <div style={Object.assign({}, ahead, { textAlign: 'right' })}>Docs</div>
                <div style={Object.assign({}, ahead, { textAlign: 'right' })}>Asked</div>
                <div style={Object.assign({}, ahead, { textAlign: 'right' })}>Paid</div>
                <div style={Object.assign({}, ahead, { textAlign: 'right' })}>Balance</div>
                <div style={ahead}></div>
              </div>
              {loading ? (
                <div style={{ padding: 24, fontSize: 12, color: INK_3 }}>Loading…</div>
              ) : filteredPackages.length === 0 ? (
                <div style={{ padding: 24, fontSize: 12, color: INK_3, textAlign: 'center' }}>
                  No sent packages match{projectFilter !== 'all' ? ' ' + projectFilter : ''}.
                </div>
              ) : filteredPackages.map(function(p) {
                return (
                  <div key={p.summaryId}
                    onClick={function() { setSelectedId(p.summaryId); setZipNote(''); }}
                    onMouseEnter={function(e) { e.currentTarget.style.background = GROUND_OFF; }}
                    onMouseLeave={function(e) { e.currentTarget.style.background = SURFACE; }}
                    style={Object.assign({}, archiveGrid, {
                      borderBottom: HAIRLINE_ROW, cursor: 'pointer',
                      paddingTop: 10, paddingBottom: 10, background: SURFACE,
                    })}>
                    <span style={dotStyle(STATE_DOT[p.state])} />
                    <div style={{ fontWeight: 500, color: INK_1, whiteSpace: 'nowrap', fontSize: 12 }}>{p.projectLabel}</div>
                    <div style={{ color: INK_2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12 }}>{p.clientName || '—'}</div>
                    <div style={{ color: INK_1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: 12 }}>
                      {p.periodLabel}
                      <span style={{ display: 'block', fontSize: 10, color: INK_3, marginTop: 1 }}>
                        Sent {fmtDate(p.sentAt)} · {billingPaidStateLabel(p.state, { hasRetainer: p.hasRetainer }).toLowerCase()}
                      </span>
                    </div>
                    <div style={Object.assign({}, numCell, { color: INK_2 })}>{p.docCount}</div>
                    <div style={Object.assign({}, numCell, { color: p.asked > 0.005 ? INK_2 : INK_4 })}>{fmtDash(p.asked)}</div>
                    <div style={Object.assign({}, numCell, { color: p.paid > 0.005 ? INK_2 : INK_4 })}>{fmtDash(p.paid)}</div>
                    <div style={Object.assign({}, numCell, p.balance > 0.005 ? { color: INK_1, fontWeight: 500 } : { color: INK_4 })}>{fmtDash(p.balance)}</div>
                    <div style={{ color: INK_4, fontSize: 11, textAlign: 'right' }}>›</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 8, flexWrap: 'wrap' }}>
          {[
            { dot: STATE_DOT.paid,    label: 'Paid in full' },
            { dot: STATE_DOT.partial, label: 'Partially paid' },
            { dot: STATE_DOT.unpaid,  label: 'Awaiting payment' },
            { dot: STATE_DOT.none,    label: 'No amount due' },
          ].map(function(l) {
            return (
              <span key={l.label} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 10, color: INK_3 }}>
                <span style={dotStyle(l.dot)} />{l.label}
              </span>
            );
          })}
          <span style={{ marginLeft: 'auto', fontSize: 10, color: INK_3 }}>
            Asked / Paid / Balance are computed live from recorded payments — never stored.
          </span>
        </div>
      </div>
    );
  }

  // ── SCREEN 2 — package preview ─────────────────────────────────────────
  function renderPreview(pkg) {
    var s = pkg.summary;
    var sends = documentSendsFor(ctx.documentSends || [], 'billing_summary', pkg.summaryId);
    var latestSendRow = sends[0] || null;
    var coverEntry = planEntryFor('cover');
    var hoursEntry = planEntryFor('hours');

    // Row snapshot expense lines (additional services + free-text expenses
    // + receipt lines) — display-only; the money already rode servicesAsked
    // via calcBillingSummaryAmount inside resolveBillingPackage.
    var addlRows = (parseJsonish(s.additional_services, []) || []).filter(function(a) { return a && (a.name || a.amount); });
    var expRows  = (parseJsonish(s.expenses, []) || []).filter(function(e) { return e && (e.description || e.amount); });
    // Expense groups (2026-08-30): fold the same way the as-sent statement
    // did — this page presents itself as "everything the client received",
    // so its rows must match the grouped lines on the cover PDF, not the
    // raw itemized receipts. Same pure helper, same expenses_display row.
    var receiptLines = applyExpenseGroupsToReceiptLines(
      buildSummaryReceiptLines(parseJsonish(s.selected_receipt_ids, []) || [], receipts),
      normalizeExpensesDisplay(s.expenses_display).groups
    );
    // The as-sent per-receipt manifest off the latest send. Feeds the
    // combined-line disclosure below as the immutable referee: it is what
    // the client was actually billed, where the live receipt rows can have
    // drifted since (descriptions live-read from receipts.notes).
    var frozenReceiptDetail = (function() {
      var p = latestSendRow && latestSendRow.payload;
      var sae = p && p.servicesAndExpenses;
      return (sae && Array.isArray(sae.receiptLinesDetail)) ? sae.receiptLinesDetail : [];
    })();
    // The groups AS THEY WERE at send time. Without these the disclosure
    // below can only ever re-derive live state and could never notice that
    // a receipt was edited, absorbed or deleted after the client was
    // billed — which is the whole question the bookkeeper is asking.
    var frozenGroups = (function() {
      var p = latestSendRow && latestSendRow.payload;
      var ed = p && p.expensesDisplay;
      return (ed && Array.isArray(ed.groups)) ? ed.groups : [];
    })();
    var frozenGroupFor = function(groupId) {
      return frozenGroups.find(function(g) { return g && String(g.id) === String(groupId); }) || null;
    };
    // Design services straight off the summary row, shown when NO hours
    // invoice resolved for the package. The design $$ rides ASKED either
    // way (calcBillingSummaryAmount reads design_amount), so hiding the
    // row made the page's sections stop adding up to the ASKED strip —
    // Cimon caught the gap on 26-Moskito May 2026 ("reference to the
    // design hours $$ is not listed", 2026-07-04).
    var bsDesignAmt   = pkg.hoursRow ? 0 : (parseFloat(s.design_amount) || 0);
    var bsDesignHours = parseFloat(s.design_hours) || 0;

    var askedParts = [];
    if (pkg.retainer && pkg.retainer.requestRecorded) askedParts.push('retainer replenishment');
    if (!pkg.hasRetainer && pkg.servicesAsked > 0.005) askedParts.push('services & expenses');
    var depCount = pkg.depositRows.filter(function(r) { return r.asked > 0.005; }).length;
    if (depCount > 0) askedParts.push(depCount + ' proposal deposit' + (depCount === 1 ? '' : 's'));
    if (pkg.invoiceRows.length > 0) askedParts.push(pkg.invoiceRows.length + ' invoice' + (pkg.invoiceRows.length === 1 ? '' : 's'));
    if (pkg.creditsApplied > 0.005) askedParts.push('less ' + fmtMoney(pkg.creditsApplied) + ' credit');

    var lastPaymentDate = paymentCards.length > 0
      ? paymentCards[paymentCards.length - 1].payment.paymentDate
      : '';

    return (
      <div>
        <div onClick={function() { setSelectedId(null); setZipNote(''); }}
          style={{ fontSize: 11, color: INK_3, marginBottom: 12, cursor: 'pointer' }}>
          ‹ All packages
        </div>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 14 }}>
          <div>
            <div style={{ fontSize: 17, fontWeight: 500, color: INK_1, letterSpacing: '-0.005em' }}>
              {pkg.projectLabel} — {pkg.monthYear}
            </div>
            <div style={{ fontSize: 11, color: INK_2, marginTop: 3 }}>
              {pkg.clientName || '—'} · {pkg.summaryType === 'off_cycle' ? 'Off-cycle package' : 'Monthly billing package'} · Sent {fmtDate(pkg.sentAt)}
              {sends.length > 1 ? ' · re-sent ' + fmtDate(latestSendRow && (latestSendRow.sentAt || latestSendRow.createdAt)) : ''}
            </div>
          </div>
          {/* Status + the recording affordance live together in the
              upper right (Cimon 2026-07-02: "Put the button on the upper
              right side of the page where it says 'Awaiting payment'").
              The button shows only when there's something to record:
              owner/admin, the package isn't fully paid, and at least one
              payable target with an open balance exists. */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, paddingTop: 4, flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap', color: STATE_TEXT[pkg.state] }}>
              <span style={dotStyle(STATE_DOT[pkg.state])} />
              {billingPaidStateLabel(pkg.state, { hasRetainer: pkg.hasRetainer })}
            </span>
            {canRecordPayment && pkg.state !== 'paid' && (paymentTargets.length > 0 || packagePendingDepositAsks(pkg).length > 0) && (
              <button onClick={function() { openRecordPaymentWizard(pkg); }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 500,
                  color: SURFACE, background: INK_1, border: 'none', borderRadius: 6, padding: '7px 16px',
                  cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
                }}>
                Record payment
              </button>
            )}
          </div>
        </div>

        {/* Roll-up strip */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', border: HAIRLINE_1PX, borderRadius: 5, marginBottom: 18, overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px' }}>
            <div style={eyebrow}>Asked</div>
            <div style={rollupVal}>{fmtDash(pkg.asked)}</div>
            <div style={rollupNote}>{askedParts.length > 0 ? askedParts.join(' + ').replace(' + less', ' less') : (pkg.hasRetainer ? 'Nothing asked — retainer covered the period' : 'Nothing asked on this package')}</div>
          </div>
          <div style={{ padding: '13px 18px', borderLeft: HAIRLINE_ROW }}>
            <div style={eyebrow}>Paid</div>
            <div style={rollupVal}>{fmtDash(pkg.paid)}</div>
            <div style={rollupNote}>
              {paymentCards.length > 0
                ? paymentCards.length + ' payment' + (paymentCards.length === 1 ? '' : 's') + (lastPaymentDate ? ' · last received ' + fmtDate(lastPaymentDate) : '')
                : (pkg.retainer && pkg.retainer.received > 0.005 ? 'Retainer deposit matched — see Retainer below' : 'No payments recorded yet')}
            </div>
          </div>
          <div style={{ padding: '13px 18px', borderLeft: HAIRLINE_ROW }}>
            <div style={eyebrow}>Balance</div>
            <div style={Object.assign({}, rollupVal, pkg.balance <= 0.005 && pkg.asked > 0.005 ? { color: ZONE_GREEN } : {})}>{fmtDash(pkg.balance)}</div>
            <div style={rollupNote}>{pkg.balance > 0.005 ? 'Still open on this package' : 'Nothing outstanding on this package'}</div>
          </div>
        </div>

        {/* Everything the client received */}
        <div style={block}>
          <div style={blockHead}>
            <span style={eyebrow}>Everything the client received</span>
            <span style={blockHint}>The complete package, frozen at send — later edits in Gilda never change what's here</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', flexWrap: 'wrap' }}>
            <button onClick={function() { downloadFullPackage(pkg); }} disabled={zipBusy}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7, fontSize: 12, fontWeight: 500,
                color: SURFACE, background: INK_1, border: 'none', borderRadius: 6, padding: '7px 18px',
                cursor: zipBusy ? 'default' : 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
                opacity: zipBusy ? 0.6 : 1,
              }}>
              {zipBusy ? 'Assembling…' : '↓ Download full package'}
              <span style={{ fontSize: 10, fontWeight: 400, color: 'rgba(255,255,255,0.65)' }}>
                {pkg.docCount} document{pkg.docCount === 1 ? '' : 's'} · zip as delivered
              </span>
            </button>
            <span style={{ fontSize: 10, color: INK_3, maxWidth: 380 }}>
              Re-assembles the envelope {pkg.clientName ? pkg.clientName.split(' ')[0] : 'the client'} received — stored as-sent PDFs where they exist, regenerated from frozen send data where not. Every document is also listed below with its own download.
            </span>
          </div>
          {zipNote && (
            <div style={{ padding: '0 14px 10px', fontSize: 10, color: ALERT_AMBER_TEXT }}>{zipNote}</div>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', flexWrap: 'wrap', borderTop: HAIRLINE_ROW }}>
            <span style={{ fontWeight: 500, fontSize: 12, color: INK_1 }}>Billing summary cover — {pkg.monthYear}</span>
            {latestSendRow && latestSendRow.sentTo && (
              <span style={{ fontSize: 10, color: INK_3 }}>to {latestSendRow.sentTo}</span>
            )}
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
              {coverEntry && entrySourceLabel(coverEntry) && (
                <span style={{ fontSize: 9.5, color: INK_4, whiteSpace: 'nowrap' }}>{entrySourceLabel(coverEntry)}</span>
              )}
              <button style={pdfBtn} onClick={function() { openCoverPdf(s); }}>View cover</button>
              <button style={pdfBtn} onClick={function() { downloadEntry(coverEntry, pkg); }} disabled={coverEntry && downloadBusy[coverEntry.filename]}>
                {entrySource(coverEntry) === 'stored' ? '↓ As-sent PDF' : '↓ PDF'}
              </button>
            </span>
          </div>
          {sends.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px', borderTop: HAIRLINE_ROW, flexWrap: 'wrap' }}>
              <span style={Object.assign({}, eyebrow, { fontSize: 9 })}>Send history · {sends.length}</span>
              {sends.map(function(send, i) {
                var when = send.sentAt || send.createdAt || '';
                return (
                  <span key={send.id} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 8, border: HAIRLINE,
                    borderRadius: 10, padding: '3px 10px', fontSize: 10, color: INK_2, whiteSpace: 'nowrap',
                  }}>
                    {i === 0 && <span style={{ color: ZONE_GREEN, fontWeight: 500 }}>Latest</span>}
                    {fmtDate(when)}
                    <button style={pillBtn}
                      title="Re-render the cover from this send's frozen payload"
                      onClick={function() { renderFrozenSnapshot(s, send); }}>View</button>
                    {send.storagePath
                      ? <button style={pillBtn}
                          title="Download the PDF blob exactly as it was sent"
                          onClick={function() { downloadStoredCover(send); }}>↓ PDF</button>
                      : <span style={{ fontSize: 9.5, color: INK_4 }}>regenerated</span>}
                  </span>
                );
              })}
            </div>
          )}
        </div>

        {/* Design services & expenses */}
        {(pkg.hoursRow || bsDesignAmt > 0.005 || addlRows.length > 0 || expRows.length > 0 || receiptLines.length > 0) && (
          <div style={block}>
            <div style={blockHead}>
              <span style={eyebrow}>Design services &amp; expenses</span>
              <span style={blockHint}>
                {pkg.hasRetainer ? 'Covered by the retainer — not part of the cash ask' : 'Billed for the period'}
              </span>
            </div>
            {pkg.hoursRow && (
              <div style={docRow}>
                <div style={docName}>
                  Design services — {pkg.monthYear}
                  <span style={docRef}>Hours invoice · {pkg.hoursRow.hoursInvoice.total_hours || 0} hrs · {pkg.hoursRow.invoiceNumber}</span>
                </div>
                <div style={statusCell}>
                  {pkg.hoursRow.retainerFunded
                    ? <span style={{ color: INK_3 }}>Retainer-funded</span>
                    : <span style={{ color: docStatusColor(pkg.hoursRow.status), whiteSpace: 'nowrap' }}>{billingDocPaidLabel(pkg.hoursRow.status)}</span>}
                </div>
                <div style={actionCell}>
                  <button style={pdfBtn} onClick={function() { downloadEntry(hoursEntry, pkg); }}>↓ PDF</button>
                  {hoursEntry && entrySourceLabel(hoursEntry) && (
                    <span style={{ fontSize: 9.5, color: INK_4 }}>{entrySourceLabel(hoursEntry)}</span>
                  )}
                </div>
                <div style={Object.assign({}, numCell, { color: INK_1, fontWeight: 500 })}>{fmtMoney(pkg.hoursRow.asked)}</div>
              </div>
            )}
            {!pkg.hoursRow && bsDesignAmt > 0.005 && (
              <div style={docRow}>
                <div style={docName}>
                  Design services — {pkg.monthYear}
                  <span style={docRef}>{bsDesignHours > 0 ? bsDesignHours + ' hrs · ' : ''}As billed on the summary cover</span>
                </div>
                <div style={statusCell}>
                  {pkg.hasRetainer
                    ? <span style={{ color: INK_3 }}>Retainer-funded</span>
                    : pkg.servicesPaid >= pkg.servicesAsked - 0.005 && pkg.servicesAsked > 0.005
                      ? <span style={{ color: ZONE_GREEN, whiteSpace: 'nowrap' }}>Paid</span>
                      : pkg.servicesPaid > 0.005
                        ? <span style={{ color: ZONE_FUCHSIA }}>Partially paid · {fmtMoney(pkg.servicesPaid)}</span>
                        : <span style={{ color: ALERT_AMBER_TEXT, whiteSpace: 'nowrap' }}>Awaiting payment</span>}
                </div>
                <div><span style={{ fontSize: 9.5, color: INK_4 }}>on cover</span></div>
                <div style={Object.assign({}, numCell, { color: INK_1, fontWeight: 500 })}>{fmtMoney(bsDesignAmt)}</div>
              </div>
            )}
            {addlRows.map(function(a, i) {
              return (
                <div key={'addl-' + i} style={docRow}>
                  <div style={docName}>{a.name || '—'}<span style={docRef}>Additional service</span></div>
                  <div />
                  <div><span style={{ fontSize: 9.5, color: INK_4 }}>on cover</span></div>
                  <div style={numCell}>{fmtMoney(parseFloat(a.amount) || 0)}</div>
                </div>
              );
            })}
            {expRows.map(function(e, i) {
              return (
                <div key={'exp-' + i} style={docRow}>
                  <div style={docName}>{e.description || '—'}<span style={docRef}>Expense</span></div>
                  <div />
                  <div><span style={{ fontSize: 9.5, color: INK_4 }}>on cover</span></div>
                  <div style={numCell}>{fmtMoney(parseFloat(e.amount) || 0)}</div>
                </div>
              );
            })}
            {receiptLines.map(function(ln) {
              // Grouped lines carry groupId and an empty receiptId, so they
              // MUST key on groupId — keying on receiptId collided every
              // grouped row against every other the moment a summary had
              // 2+ groups (latent until multi-group shipped 2026-09-04).
              var isGroup = !!ln.groupId;
              var rowKey  = isGroup ? 'grp-' + ln.groupId : 'rcpt-' + ln.receiptId;
              if (!isGroup) {
                return (
                  <div key={rowKey} style={docRow}>
                    <div style={docName}>{ln.description}<span style={docRef}>Reimbursable expense{ln.date ? ' · ' + ln.date : ''}</span></div>
                    <div />
                    <div><span style={{ fontSize: 9.5, color: INK_4 }}>on cover</span></div>
                    <div style={numCell}>{fmtMoney(ln.amount)}</div>
                  </div>
                );
              }
              // Combined line: the client saw ONE row. The bookkeeper needs
              // to see what went into it, so it discloses its members.
              var detail   = expenseGroupMembers(ln, receipts, frozenReceiptDetail, frozenGroupFor(ln.groupId));
              // Namespaced by summary — group ids are uuids in practice, but
              // legacy id-less groups normalize to 'auto0' on every summary,
              // which would render package B pre-expanded from package A.
              var openKey  = pkg.summaryId + ':' + ln.groupId;
              var isOpen   = !!openExpenseGroups[openKey];
              var memberN  = detail.members.length;
              // The row is labelled "on cover", so it must show what the
              // client was BILLED. The folded line is a live recompute and
              // can have moved since; prefer the as-sent figure when the
              // frozen manifest has one.
              var coverAmt = detail.asSentTotal == null ? ln.amount : detail.asSentTotal;
              return (
                <div key={rowKey}>
                  <div style={Object.assign({}, docRow, { cursor: 'pointer' })}
                       data-testid={'bh-expense-group-' + ln.groupId}
                       onClick={function() {
                         setOpenExpenseGroups(function(prev) {
                           var next = Object.assign({}, prev);
                           if (next[openKey]) delete next[openKey]; else next[openKey] = true;
                           return next;
                         });
                       }}>
                    <div style={docName}>
                      <span style={{ color: INK_4, marginRight: 6, fontSize: 10 }}>{isOpen ? '▾' : '▸'}</span>
                      {ln.description}
                      <span style={docRef}>
                        Combined line · {memberN} receipt{memberN === 1 ? '' : 's'}
                      </span>
                      {!detail.totalsAgree && (
                        <span style={{ display: 'block', marginTop: 1, fontSize: 10, color: ALERT_AMBER_TEXT }}>
                          Receipts now total {fmtMoney(detail.memberTotal)} — edited since this was sent
                        </span>
                      )}
                    </div>
                    <div />
                    <div><span style={{ fontSize: 9.5, color: INK_4 }}>on cover</span></div>
                    <div style={numCell}>{fmtMoney(coverAmt)}</div>
                  </div>
                  {isOpen && (
                    <div data-testid={'bh-expense-group-members-' + ln.groupId}
                         style={{ background: GROUND_OFF, borderBottom: HAIRLINE_ROW }}>
                      {/* Labelled, because the two money columns are NOT the
                          same number: the vendor's paperwork says COST, the
                          client was billed cost x 1.15. Unlabelled, a
                          bookkeeper matching a receipt has no way to tell
                          which figure to look for. */}
                      <div style={Object.assign({}, docRow, {
                             padding: '6px 14px 5px 34px', borderBottom: HAIRLINE_ROW,
                           })}>
                        <div style={eyebrow}>Receipt</div>
                        <div style={eyebrow}>Vendor</div>
                        <div style={Object.assign({}, eyebrow, { textAlign: 'right' })}>Cost</div>
                        <div style={Object.assign({}, eyebrow, { textAlign: 'right' })}>Billed (+ markup)</div>
                      </div>
                      {detail.members.map(function(m, mi) {
                        return (
                          <div key={'gm-' + ln.groupId + '-' + (m.receiptId || ('idx' + mi))}
                               style={Object.assign({}, docRow, { padding: '7px 14px 7px 34px', borderBottom: 'none' })}>
                            <div style={{ fontSize: 11.5, color: INK_2 }}>
                              {m.description || '—'}
                              <span style={docRef}>
                                {m.date || 'no date'}
                                {m.source === 'frozen' ? ' · as-sent record' : ''}
                              </span>
                              {m.source === 'missing' && (
                                <span style={{ display: 'block', fontSize: 10, color: ALERT_AMBER_TEXT }}>
                                  Receipt no longer on file — amount from the as-sent record
                                </span>
                              )}
                              {m.mismatch && (
                                <span style={{ display: 'block', fontSize: 10, color: ALERT_AMBER_TEXT }}>
                                  Billed {fmtMoney(m.asSentAmount)}; the receipt now reads {fmtMoney(m.amount)}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 11.5, color: INK_2, whiteSpace: 'nowrap',
                                          overflow: 'hidden', textOverflow: 'ellipsis' }}
                                 title={m.vendor || ''}>
                              {m.vendor || '—'}
                            </div>
                            <div style={Object.assign({}, numCell, { fontSize: 11.5, color: INK_3 })}>
                              {m.cost ? fmtMoney(m.cost) : '—'}
                            </div>
                            <div style={Object.assign({}, numCell, { fontSize: 11.5, color: INK_2 })}>{fmtMoney(m.amount)}</div>
                          </div>
                        );
                      })}
                      <div style={Object.assign({}, docRow, { padding: '7px 14px 9px 34px', borderBottom: 'none' })}>
                        <div style={{ fontSize: 10, color: INK_3, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                          Combined total
                        </div>
                        <div />
                        <div style={Object.assign({}, numCell, { fontSize: 11.5, color: INK_3 })}>
                          {detail.memberCostTotal ? fmtMoney(detail.memberCostTotal) : '—'}
                        </div>
                        <div style={Object.assign({}, numCell, { fontSize: 11.5, color: INK_1, fontWeight: 500 })}>
                          {fmtMoney(detail.memberTotal)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            {/* On non-retainer projects the services money is part of the
                cash ask — close the section on its total so the page's
                sections visibly add up to the ASKED strip. Retainer
                projects skip it (covered by the retainer; the Retainer
                block below carries the cash story). */}
            {!pkg.hasRetainer && pkg.servicesAsked > 0.005 && (
              <div style={subtotalRow}>
                <div style={subtotalLbl}>Services &amp; expenses total</div>
                <div style={Object.assign({}, numCell, { fontWeight: 500, color: INK_1 })}>{fmtMoney(pkg.servicesAsked)}</div>
              </div>
            )}
          </div>
        )}

        {/* Retainer */}
        {pkg.retainer && (
          <div style={block}>
            <div style={blockHead}>
              <span style={eyebrow}>Retainer</span>
            </div>
            {pkg.retainer.requestRecorded ? (
              <div style={docRow}>
                <div style={docName}>
                  Retainer replenishment requested
                  <span style={docRef}>As recorded on this summary at send</span>
                </div>
                <div style={statusCell}>
                  {pkg.retainer.received >= pkg.retainer.requested - 0.005 ? (
                    <span style={{ color: ZONE_GREEN, whiteSpace: 'nowrap' }}>Paid in full</span>
                  ) : pkg.retainer.received > 0.005 ? (
                    <span style={{ color: ZONE_FUCHSIA }}>Partially received · {fmtMoney(pkg.retainer.received)}</span>
                  ) : (
                    <span style={{ color: ALERT_AMBER_TEXT, whiteSpace: 'nowrap' }}>Awaiting payment</span>
                  )}
                  {/* Heuristic note — wraps to as many lines as it needs. */}
                  <span style={statusNote}>
                    Matched from retainer deposits dated after this summary went out — verify against the retainer ledger
                  </span>
                </div>
                <div />
                <div style={Object.assign({}, numCell, { color: INK_1, fontWeight: 500 })}>{fmtMoney(pkg.retainer.requested)}</div>
              </div>
            ) : (
              <div style={{ padding: '10px 14px', fontSize: 11, color: INK_3 }}>
                No replenishment was requested on this summary — the retainer covered the period.
              </div>
            )}
          </div>
        )}

        {/* Proposals — deposits requested. The proposal row IS the deposit
            on this surface; the per-item DEP records behind it are internal
            allocation plumbing and are never mentioned in the copy. A
            proposal awaiting payment normally has no DEP records yet —
            that's the expected mid-lifecycle state (approval mints them
            when the client pays), so the asked amount shows regardless. */}
        {pkg.depositRows.length > 0 && (
          <div style={block}>
            <div style={blockHead}>
              <span style={eyebrow}>Proposals — deposits requested ({pkg.depositRows.length})</span>
            </div>
            {pkg.depositRows.map(function(row) {
              var p = row.proposal;
              var pEntry = planEntryFor('proposal', p.id);
              // Sub-note per asked source (askedSource itself stays
              // internal — the copy speaks in lifecycle terms only).
              var askedNote = '';
              if (row.askedSource === 'frozen') askedNote = 'As sent — awaiting client payment';
              else if (row.askedSource === 'stored') askedNote = 'From the proposal record — amount may differ from what was sent';
              else if (row.askedSource === 'none') askedNote = 'No deposit amount on record for this proposal';
              return (
                <div key={p.id} style={docRow}>
                  <div style={docName}>
                    {p.name || p.proposal_ref || 'Proposal'}
                    {/* Ref only — never a proposal-level deposit percentage.
                        Items on one proposal carry DIFFERENT deposit
                        requirements (80% standard, 100% in-stock, per-item
                        overrides), so no single percentage exists; the
                        deposit AMOUNT is the only honest proposal-level
                        number (Cimon, 2026-07-02). */}
                    <span style={docRef}>{p.proposal_ref || ''}</span>
                  </div>
                  <div style={statusCell}>
                    {row.askedSource === 'dep'
                      ? <span style={{ color: docStatusColor(row.status), whiteSpace: 'nowrap' }}>{billingDocPaidLabel(row.status)}</span>
                      : <span style={{ color: ALERT_AMBER_TEXT, whiteSpace: 'nowrap' }}>Awaiting payment</span>}
                    {row.askedSource === 'dep' && row.status === 'partial' && (
                      <span style={statusNote}>{fmtMoney(row.paid)} received so far</span>
                    )}
                    {askedNote && <span style={statusNote}>{askedNote}</span>}
                  </div>
                  <div style={actionCell}>
                    <button style={pdfBtn} onClick={function() { downloadEntry(pEntry, pkg); }}>↓ PDF</button>
                    {pEntry && entrySourceLabel(pEntry) && (
                      <span style={{ fontSize: 9.5, color: INK_4 }}>{entrySourceLabel(pEntry)}</span>
                    )}
                  </div>
                  <div style={numCell}>{row.askedSource === 'none' ? '—' : fmtMoney(row.asked)}</div>
                </div>
              );
            })}
            <div style={subtotalRow}>
              <div style={subtotalLbl}>Deposits requested</div>
              <div style={Object.assign({}, numCell, { fontWeight: 500, color: INK_1 })}>{fmtMoney(pkg.depositAskedTotal)}</div>
            </div>
          </div>
        )}

        {/* Invoices on the package — tagged rows, plus rows reconciled from
            the frozen send manifest (linkedByRef: live row found by ref but
            never tagged; frozenOnly: on the manifest with no live row). */}
        {pkg.invoiceRows.length > 0 && (
          <div style={block}>
            <div style={blockHead}>
              <span style={eyebrow}>Invoices on this package ({pkg.invoiceRows.length})</span>
              <span style={blockHint}>One final invoice per purchase order — balance due as each vendor's order wraps</span>
            </div>
            {pkg.manifestMismatch && (
              <div style={{ padding: '8px 14px', fontSize: 10, color: INK_3, borderBottom: HAIRLINE_ROW }}>
                {pkg.manifestLinkedCount + pkg.frozenOnlyCount} document{pkg.manifestLinkedCount + pkg.frozenOnlyCount === 1 ? '' : 's'} on this package's send record {pkg.manifestLinkedCount + pkg.frozenOnlyCount === 1 ? "isn't" : "aren't"} tagged to this summary in Gilda — shown below from the send record. Tagging them to the summary would link payments cleanly.
              </div>
            )}
            {pkg.invoiceRows.map(function(row) {
              var inv = row.invoice;
              var entryId = inv ? inv.id : 'ref:' + row.ref;
              var iEntry = planEntryFor('invoice', entryId);
              var hasDownload = !!(iEntry && iEntry.attempts && iEntry.attempts.length > 0);
              return (
                <div key={entryId} style={docRow}>
                  <div style={docName}>
                    {row.name}
                    <span style={docRef}>{row.ref || ''}</span>
                  </div>
                  <div style={statusCell}>
                    {row.frozenOnly ? (
                      <span style={{ color: INK_3 }}>Payment state unknown</span>
                    ) : (
                      <span style={{ color: docStatusColor(row.status), whiteSpace: 'nowrap' }}>{billingDocPaidLabel(row.status)}</span>
                    )}
                    {row.frozenOnly && (
                      <span style={statusNote}>On the send record only — no matching record in Gilda today</span>
                    )}
                    {row.linkedByRef && (
                      <span style={statusNote}>Linked from the send record — not tagged to this summary in Gilda</span>
                    )}
                  </div>
                  <div style={actionCell}>
                    {hasDownload
                      ? (
                        <React.Fragment>
                          <button style={pdfBtn} onClick={function() { downloadEntry(iEntry, pkg); }}>↓ PDF</button>
                          {entrySourceLabel(iEntry) && (
                            <span style={{ fontSize: 9.5, color: INK_4 }}>{entrySourceLabel(iEntry)}</span>
                          )}
                        </React.Fragment>
                      )
                      : <span style={{ fontSize: 9.5, color: INK_4 }}>{row.frozenOnly ? 'no PDF available' : 'on cover'}</span>}
                  </div>
                  <div style={numCell}>{fmtMoney(row.asked)}</div>
                </div>
              );
            })}
            <div style={subtotalRow}>
              <div style={subtotalLbl}>Invoices total</div>
              <div style={Object.assign({}, numCell, { fontWeight: 500, color: INK_1 })}>{fmtMoney(pkg.invoiceAskedTotal)}</div>
            </div>
            {pkg.creditsApplied > 0.005 && (
              <div style={subtotalRow}>
                <div style={subtotalLbl}>Less client credit applied</div>
                <div style={Object.assign({}, numCell, { color: INK_2 })}>−{fmtMoney(pkg.creditsApplied)}</div>
              </div>
            )}
          </div>
        )}

        <div style={{ fontSize: 10, color: INK_3, margin: '4px 2px 14px' }}>
          ↓ PDF downloads the exact file the client received when a stored copy exists. "Regenerated" documents have no stored PDF — Gilda re-renders them from the frozen send data; "from live data" means no frozen copy exists either, so the render reflects today's records.
        </div>

        {/* Recorded client payments */}
        <div style={block}>
          <div style={blockHead}>
            <span style={eyebrow}>Recorded client payments ({paymentCards.length})</span>
            <span style={blockHint}>Each payment is spelled out exactly as it was applied</span>
          </div>
          {paymentCards.length === 0 ? (
            <div style={{ padding: '12px 14px', fontSize: 11, color: INK_3 }}>
              No client payments have been recorded against this package's documents yet.
            </div>
          ) : paymentCards.map(function(card) {
            var pm = card.payment;
            var meta = [pm.method ? pm.method.charAt(0).toUpperCase() + pm.method.slice(1) : '', fmtDate(pm.paymentDate)]
              .filter(Boolean).join(' · ');
            var src = [pm.bankReference ? 'Bank ref ' + pm.bankReference : '', pm.reference || '']
              .filter(Boolean).join(' · ');
            return (
              <div key={pm.id} style={{ padding: '12px 14px', borderBottom: HAIRLINE_ROW }}>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                  <span style={Object.assign({}, { fontSize: 13, fontWeight: 500, color: INK_1 }, moneyFont)}>{fmtMoney(pm.amountTotal)}</span>
                  <span style={{ fontSize: 11, color: INK_2 }}>{meta || '—'}</span>
                  {src && <span style={{ fontSize: 10, color: INK_3, marginLeft: 'auto', textAlign: 'right' }}>{src}</span>}
                </div>
                <div style={{ marginTop: 8, borderLeft: HAIRLINE, paddingLeft: 12, marginLeft: 2 }}>
                  {card.lines.map(function(ln, i) {
                    return (
                      <div key={i} style={{ display: 'grid', gridTemplateColumns: '96px 14px 1fr', columnGap: 8, padding: '2.5px 0', fontSize: 11, alignItems: 'baseline' }}>
                        <span style={Object.assign({}, { textAlign: 'right', color: INK_1 }, moneyFont)}>{fmtMoney(ln.amount)}</span>
                        <span style={{ color: INK_4, textAlign: 'center' }}>→</span>
                        <span style={{ color: INK_2 }}>
                          {ln.label}
                          {ln.ref && <span style={{ fontSize: 10, color: INK_3, marginLeft: 5 }}>{ln.ref}</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div style={{ fontSize: 10, color: INK_3, marginTop: 6 }}>
                  {card.fullyApplied
                    ? 'Fully applied — nothing left unallocated on this payment.'
                    : fmtMoney(Math.abs(card.unallocated)) + (card.unallocated > 0 ? ' of this payment is not yet allocated to any document.' : ' allocated beyond the recorded payment amount — worth a look.')}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // ── COVER PREVIEW MODAL (kept from v1, dark chrome) ────────────────────
  function renderCoverModal() {
    if (!previewing) return null;
    return (
      <div onClick={function(e) { if (e.target === e.currentTarget) closePreview(); }}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 1100, display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#1a1a1a' }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#fff' }}>
            {previewing.summary.project_label} · {periodLabel(previewing.summary.month, previewing.summary.year)}
            {previewing.frozen && (
              <span title="This cover was regenerated from the frozen document_sends payload — not live data."
                style={{ marginLeft: 10, padding: '2px 8px', fontSize: 9, fontWeight: 700,
                  border: '0.5px solid #4FC3A1', borderRadius: 10, color: '#4FC3A1',
                  textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                As sent
              </span>
            )}
          </span>
          <div style={{ display: 'flex', gap: 8 }}>
            {previewing.url && (
              <a href={previewing.url} download={(previewing.summary.project_label || 'package') + '_cover.pdf'}
                style={{ padding: '6px 14px', background: '#fff', color: '#1a1a1a', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', textDecoration: 'none', fontFamily: 'inherit' }}>
                ↓ Download
              </a>
            )}
            <button onClick={closePreview}
              style={{ padding: '6px 14px', background: 'none', border: '1px solid #555', color: '#fff', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' }}>
              Close
            </button>
          </div>
        </div>

        {/* Send history strip — one pill per send event, most recent first.
            View re-renders the iframe with that send's frozen payload;
            ↓ PDF fetches the storage_path blob from the billing-summaries
            bucket. */}
        {(function() {
          var sends = documentSendsFor(ctx.documentSends || [], 'billing_summary', previewing.summary.id);
          if (sends.length === 0) return null;
          return (
            <div style={{ background: '#222', borderTop: '0.5px solid #444', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: 10, overflowX: 'auto' }}>
              <span style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.07em', whiteSpace: 'nowrap', flexShrink: 0 }}>
                Send history · {sends.length}
              </span>
              {sends.map(function(send, i) {
                var when = send.sentAt || send.createdAt || '';
                var label = fmtDate(when) || when || '—';
                return (
                  <div key={send.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: '#333', border: '0.5px solid #555', borderRadius: 12, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: i === 0 ? '#4FC3A1' : '#bbb' }}>
                      {i === 0 ? 'Latest · ' : ''}{label}
                    </span>
                    {send.sentTo && (
                      <span style={{ fontSize: 9, color: '#888' }}>→ {send.sentTo}</span>
                    )}
                    <button
                      onClick={function() { renderFrozenSnapshot(previewing.summary, send); }}
                      title="Re-render the cover from this send's frozen payload"
                      style={{ padding: '2px 8px', fontSize: 10, color: '#fff', background: 'none', border: '0.5px solid #555', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
                      View
                    </button>
                    {send.storagePath && (
                      <button
                        onClick={function() { downloadStoredCover(send); }}
                        title="Download the PDF blob exactly as it was sent"
                        style={{ padding: '2px 8px', fontSize: 10, color: '#fff', background: 'none', border: '0.5px solid #555', borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit' }}>
                        ↓ PDF
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        <div style={{ flex: 1, position: 'relative', background: '#444' }}>
          {previewing.loading ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13 }}>
              Regenerating cover from saved data…
            </div>
          ) : previewing.err ? (
            <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#FFB4AB', fontSize: 13, padding: 20 }}>
              {previewing.err}
            </div>
          ) : previewing.url ? (
            <iframe src={previewing.url}
              style={{ width: '100%', height: '100%', border: 'none' }}
              title="Cover preview" />
          ) : null}
        </div>
      </div>
    );
  }
}

// ── Shared style fragments (design-token composed) ──────────────────────────
var moneyFont = { fontVariantNumeric: 'tabular-nums', fontFeatureSettings: TABULAR };

function dotStyle(color) {
  return { display: 'inline-block', width: 7, height: 7, borderRadius: '50%', flexShrink: 0, background: color };
}

var sel = {
  fontFamily: 'inherit', fontSize: 11, color: INK_2, background: SURFACE,
  border: HAIRLINE, borderRadius: 4, padding: '5px 9px', outline: 'none',
};

var archiveGrid = {
  display: 'grid',
  gridTemplateColumns: '26px 108px 118px minmax(220px,1fr) 52px 112px 112px 112px 14px',
  alignItems: 'center', columnGap: 10, padding: '0 14px',
};

var ahead = {
  fontSize: 9, fontWeight: 500, color: INK_3, letterSpacing: '0.07em',
  textTransform: 'uppercase', padding: '8px 0', whiteSpace: 'nowrap',
};

var numCell = Object.assign({
  textAlign: 'right', whiteSpace: 'nowrap', color: INK_2, fontSize: 12,
}, moneyFont);

var eyebrow = {
  fontSize: 10, fontWeight: 500, color: INK_3, letterSpacing: '0.07em', textTransform: 'uppercase',
};

var rollupVal = Object.assign({ fontSize: 17, fontWeight: 500, color: INK_1, marginTop: 3 }, moneyFont);
var rollupNote = { fontSize: 10, color: INK_3, marginTop: 2 };

var block = { border: HAIRLINE_1PX, borderRadius: 5, marginBottom: 14, overflow: 'hidden', background: SURFACE };

var blockHead = {
  display: 'flex', alignItems: 'baseline', gap: 10, padding: '9px 14px',
  borderBottom: HAIRLINE, background: GROUND_OFF,
};

var blockHint = { fontSize: 10, color: INK_3, marginLeft: 'auto', textAlign: 'right' };

// Document-row grid. The status column is a bounded flexible track and the
// action column has a fixed floor — the two can never overlap: status text
// wraps inside its own cell (minWidth: 0) instead of running under the
// download button (2026-07-02 field fix).
var docRow = {
  display: 'grid', gridTemplateColumns: 'minmax(220px,1fr) minmax(170px,240px) 112px 112px',
  alignItems: 'center', columnGap: 12, padding: '9px 14px', borderBottom: HAIRLINE_ROW,
};

var docName = { fontWeight: 500, color: INK_1, fontSize: 12 };
var docRef = { display: 'block', marginTop: 1, fontWeight: 400, fontSize: 10, color: INK_3 };

// Status cell: minWidth 0 lets long notes wrap within the grid track
// instead of overflowing into the action column.
var statusCell = { fontSize: 11, minWidth: 0 };
var statusNote = {
  display: 'block', fontSize: 10, color: INK_3, marginTop: 2,
  whiteSpace: 'normal', lineHeight: 1.4,
};

// Action cell: button with its source label stacked BELOW it, never beside
// (the beside layout is what collided with long status text).
var actionCell = {
  display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2, minWidth: 0,
};

var subtotalRow = {
  display: 'grid', gridTemplateColumns: 'minmax(220px,1fr) minmax(170px,240px) 112px 112px',
  columnGap: 12, padding: '8px 14px', borderTop: HAIRLINE,
};
var subtotalLbl = Object.assign({}, eyebrow, { fontSize: 10, gridColumn: '1 / 4', alignSelf: 'center' });

var pdfBtn = {
  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 9.5, fontWeight: 500,
  color: INK_2, border: HAIRLINE, borderRadius: 3, padding: '3px 8px',
  background: SURFACE, cursor: 'pointer', whiteSpace: 'nowrap', fontFamily: 'inherit',
};

var pillBtn = {
  fontSize: 9.5, fontWeight: 500, color: INK_2, border: HAIRLINE, borderRadius: 3,
  padding: '1px 7px', background: SURFACE, cursor: 'pointer', fontFamily: 'inherit',
};
